// controllers/scorm_api_controller.js
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["frame", "modal", "score"];

  static values = {
    cmiData: String,
    persistenceUrl: String,
    score: Number,
    status: String,
    studentId: Number,
    studentName: String,
    targetStatus: String,
    trainingCourseId: Number,
    trainingProgressId: Number
  }

  connect() {
    this._debugMode = true; // Enable debug mode for detailed logging
    this._debug("SCORM API Controller connected. Attaching API to window.");

    this.isInitialized = false;
    this.isTerminated = false;
    this.lastErrorCode = "0";
    this.targetStatusValue = this.targetStatusValue || "passed";
    this.cmiData = JSON.parse(this.cmiDataValue || "{}");

    // this.statusValue = this.statusValue || "incomplete"; // Default status
    this.cmiData["cmi.core.lesson_status"] = this.statusValue || "incomplete";
    this.cmiData["cmi.core.student_id"] = this.studentIdValue || "";
    this.cmiData["cmi.core.student_name"] = this.studentNameValue || "";

    window.API = {
      LMSInitialize: (param) => this.LMSInitialize(param),
      LMSFinish: (param) => this.LMSFinish(param),
      LMSGetValue: (element) => this.LMSGetValue(element),
      LMSSetValue: (element, value) => this.LMSSetValue(element, value),
      LMSCommit: (param) => this.LMSCommit(param),
      LMSGetLastError: () => this.LMSGetLastError(),
      LMSGetErrorString: (errorCode) => this.LMSGetErrorString(errorCode),
      LMSGetDiagnostic: (errorCode) => this.LMSGetDiagnostic(errorCode),
    };

    // const controller = this;
    // this.frameTarget.addEventListener("load", function() {
    //   this.contentDocument.querySelector("button#prev").addEventListener("click", controller._navButtonClicked.bind(controller));
    //   this.contentDocument.querySelector("button#next").addEventListener("click", controller._navButtonClicked.bind(controller));
    // });

    this._debug("SCORM API ready on window.API");
  }

  disconnect() {
    this._debug("SCORM API Controller disconnected. Removing API from window.");

    if (window.API) {
      delete window.API;
    }
    this.isTerminated = true;
  }

  // Basic SCORM API methods

  LMSInitialize() {
    this._clearError();
    this._debug("LMSInitialize successful");

    this.isInitialized = true;
    this.isTerminated = false;
    return "true";
  }

  LMSFinish() {
    this._clearError();
    this._persistData().then(success => {
      if (!success) {
        console.error("Asynchronous persistence failed during LMSFinish.");
      }
    });

    this.isInitialized = false;
    this.isTerminated = true;
    return "true";
  }

  LMSGetValue(key) {
    if (this.cmiData.hasOwnProperty(key)) {
      this._debug(`  Returning value: "${this.cmiData[key]}"`);
      return String(this.cmiData[key]);
    }
    this._debug(`  Key "${key}" not found in CMI data.`);
    return "";
  }

  LMSSetValue(key, value) {
    value = String(value);
    this.cmiData[key] = value;
    // Handle status changes using stimulus, see #statusValueChanged
    if (key === "cmi.core.lesson_status") {
      this.statusValue = value;
    }
    else if (key === "cmi.core.score.raw") {
      this.scoreValue = value;
    }
    this._debug(`  Stored value "${value}" for key "${key}"`);
    return "true";
  }

  LMSCommit() {
    this._debug("LMS Commit");
    this._clearError();

    this._persistData().then(success => {
      if (!success) {
        console.error("Asynchronous persistence failed after LMSCommit returned.");
      }
    });

    this._debug("LMSCommit returning 'true' (Persistence initiated).");
    return "true";
  }

  LMSGetLastError() {
    return "0";
  }

  LMSGetErrorString(errorCode) {
    return "No error";
  }

  LMSGetDiagnostic(errorCode) {
    return "No diagnostic info";
  }

  _debug(message) {
    if (this._debugMode) {
      console.log("SCORM-API DEBUG:", message);
    }
  }

  _setError(code) {
    this.lastErrorCode = String(code);
    console.error(`SCORM Error Set: ${code} - ${this.LMSGetErrorString(String(code))}`);
  }

  _clearError() {
    this.lastErrorCode = "0";
  }

  _getCsrfToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement ? tokenElement.getAttribute('content') : null;
  }

  async _persistData() {
    if (!this.hasPersistenceUrlValue || !this.persistenceUrlValue) {
      this._debug("Persistence URL not set, skipping server save.");
      return true;
    }

    if (!this.isInitialized && !this.isTerminated) {
        console.warn("Attempted to persist data while not initialized. Skipping.");
        return false;
    }

    this._debug(`Attempting to persist SCORM data to: ${this.persistenceUrlValue}`);
    const dataToPersist = { ...this.cmiData, training_progress_id: this.trainingProgressIdValue };
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': this._getCsrfToken()
    };

    try {
      const response = await fetch(this.persistenceUrlValue, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(dataToPersist)
      });

      if (response.ok) {
        this._debug(`SCORM data successfully persisted. Status: ${response.status}`);
        return true;
      }
      else {
        console.error(`Failed to persist SCORM data. Server responded with status: ${response.status} ${response.statusText}`);
        try {
           const errorBody = await response.text();
           console.error("Server error response body:", errorBody);
        } catch (e) {
           console.error("Could not read error response body.");
        }
        return false;
      }
    } catch (error) {
      console.error("Network error or exception during SCORM data persistence:", error);
      return false;
    }
  }

  _showCompletionModal() {
    this._debug("Showing completion modal");
    $(this.modalTarget).modal("show");
  }

  // _navButtonClicked(event) {
  //   this._debug("Nav button clicked");
  //   this._debug(event);
  //   this.currentSlide = event.view.DS.appState.windowManager.windows[0].getCurrentSlide();
  //   this._debug(this.currentSlide);
  // }

  scoreValueChanged() {
    this._debug(`Score value changed to: ${this.scoreValue}`);
    if (this.hasScoreTarget) {
      this.scoreTarget.innerHTML = this.scoreValue;
    }
  }

  statusValueChanged() {
    this._debug(`Status value changed to: ${this.statusValue}`);

    this.element.dataset.scormStatus = this.statusValue;

    this.LMSCommit();
    this._debug("LMSCommit called after status value change");

    if (this.statusValue === this.targetStatusValue) {
      this._showCompletionModal();
    }
  }
}
